import React, { useEffect, useState } from 'react';
import { graphql } from 'react-apollo';
import { flowRight as compose } from 'lodash';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './AdditionalFeesContainer.css';
import Loader from '../../../components/Common/Loader/Loader';
import { Form, InputGroup, Modal } from 'react-bootstrap';
import { Field, reduxForm, change } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import getSiteSettings from '../../site-admin/configSettings/getSiteSettings.graphql';
import getAdditionalFees from './getAdditionalFees.graphql';
import updateAdditionalFees from './updateAdditionalFees.graphql';

import messages from '../../../locale/messages';
import { FormattedMessage, injectIntl } from 'react-intl';

const renderFeeField = ({ input, label, type, meta: { touched, error }, isEnabled, symbol, symbolClassName }) => {
    if (!isEnabled) {
        return null;
    }

    return (
        <div className={s.paddingRoutesSection}>
            <Form.Group>
                <Form.Label>{label}</Form.Label>
                <InputGroup style={{ maxWidth: '900px' }}>
                    <InputGroup.Prepend>
                        <InputGroup.Text className={symbolClassName} style={{ padding: "0px 20px" }}> {symbol} </InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                        className='py-4 fs-5'
                        {...input}
                        placeholder={`Enter ${label.toLowerCase()}`}
                        type={type}
                        style={{ maxWidth: '300px' }}
                    />
                </InputGroup>
                {touched && error && <span className={s.errorMessage}>{error}</span>}
            </Form.Group>
        </div>
    );
};

const AdditionalFeesContainer = ({
    getAdditionalFees: { loading, getAdditionalFees, refetch },
    mutate,
    handleSubmit,
    submitting,
    invalid,
    dispatch,
    getSiteSettings,
    intl

}) => {
    const [isPackagingFeeEnabled, setPackagingFeeEnabled] = useState(false);
    const [isParkingFeeEnabled, setParkingFeeEnabled] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
    const { formatMessage } = intl;

    const emailSupport = getSiteSettings?.getSiteSettings?.results?.find(setting => setting.name === "contactEmail")?.value || '';
    const phoneSupport = getSiteSettings?.getSiteSettings?.results?.find(setting => setting.name === "contactNumber")?.value || '';

    useEffect(() => {
        if (getAdditionalFees?.result) {
            const {
                packagingFee,
                parkingFee,
                isPackagingFeeEnabled,
                isParkingFeeEnabled,
                packagingFeeLastUpdatedAt,
                parkingFeeLastUpdatedAt
            } = getAdditionalFees.result;

            dispatch(change('AdditionalFeesForm', 'packagingFee', packagingFee));
            dispatch(change('AdditionalFeesForm', 'parkingFee', parkingFee));
            setPackagingFeeEnabled(isPackagingFeeEnabled);
            setParkingFeeEnabled(isParkingFeeEnabled);
            // Check if either of the updatedAt fields is not null
            if (packagingFeeLastUpdatedAt || parkingFeeLastUpdatedAt) {
                setHasBeenUpdated(true);
            }
        }
    }, [getAdditionalFees, dispatch]);

    if (loading) {
        return <Loader type={"page"} show={loading}></Loader>
    }

    const onSubmit = async (values) => {
        const { packagingFee, parkingFee } = values;
        const { data } = await mutate({
            variables: {
                packagingFee: parseFloat(packagingFee),
                parkingFee: parseFloat(parkingFee),
                isPackagingFeeEnabled,
                isParkingFeeEnabled
            }
        });

        if (data && data.updateAdditionalFees) {
            if (data.updateAdditionalFees.status === '200') {
                toastr.success('Success', 'Additional fees updated successfully!');
                await refetch(); // Refetch the query to update the UI

            } else {
                toastr.error('Error', data.updateAdditionalFees.errorMessage);
            }
        }
    };

    return (
        <div className="p-4 container pb-5 mt-5" >
            <div className={"bg-white p-4 mt-5 mt-md-0"} style={{ borderRadius: "20px", marginTop: "50px" }}>
                <h3 className='mb-4'>
                    Manage your additional fees
                </h3>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Group className={s.paddingRoutesSection}>
                        <Form.Check
                            type="switch"
                            id="packaging-fee-switch"
                            label="Enable Packaging Fee"
                            checked={isPackagingFeeEnabled}
                            onChange={() => setPackagingFeeEnabled(!isPackagingFeeEnabled)}
                            className="yellow-switch"
                        />
                    </Form.Group>

                    <Field
                        name="packagingFee"
                        type="number"
                        label="Packaging Fee"
                        component={renderFeeField}
                        isEnabled={isPackagingFeeEnabled}
                        symbol="Rp"
                        symbolClassName="bg-primary text-white"
                    />

                    <Form.Group className={s.paddingRoutesSection}>
                        <Form.Check
                            type="switch"
                            id="parking-fee-switch"
                            label="Enable Parking Fee"
                            checked={isParkingFeeEnabled}
                            onChange={() => setParkingFeeEnabled(!isParkingFeeEnabled)}
                        />
                    </Form.Group>

                    <Field
                        name="parkingFee"
                        type="number"
                        label="Parking Fee"
                        component={renderFeeField}
                        isEnabled={isParkingFeeEnabled}
                        symbol="Rp"
                        symbolClassName="bg-primary text-white"
                    />

                    <div className="d-flex align-items-center">
                        {hasBeenUpdated ? (
                            <div>
                                <p><FormattedMessage {...messages.contactSupport} /></p>
                                <button
                                    type="button"
                                    className="btn btn-danger text-white ms-2"
                                    onClick={() => setShowInfoModal(true)}
                                >
                                    Contact support
                                    <i className="fa fa-info-circle"></i>
                                </button>
                            </div>
                        ) : (
                            <button className='btn btn-danger ms-2' type="submit" disabled={submitting || invalid || hasBeenUpdated}>
                                {submitting ? 'Saving...' : 'Save Fees'}
                            </button>

                        )}
                    </div>
                </form>
            </div>

            <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>ASAP CONTACT SUPPORT</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Email: {emailSupport}</p>
                    <p>Phone: {phoneSupport}</p>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowInfoModal(false)}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const validate = (values) => {
    const errors = {};
    if (values.packagingFee) {
        if (isNaN(values.packagingFee) || Number(values.packagingFee) < 0) {
            errors.packagingFee = 'Please enter a valid packaging fee';
        }
    }
    if (values.parkingFee) {
        if (isNaN(values.parkingFee) || Number(values.parkingFee) < 0) {
            errors.parkingFee = 'Please enter a valid parking fee';
        }
    }
    return errors;
};

export default compose(
    withStyles(s),
    injectIntl,
    reduxForm({
        form: 'AdditionalFeesForm',
        validate,
        enableReinitialize: true,
        initialValues: props => ({
            packagingFee: props.getAdditionalFees?.result?.packagingFee || '',
            parkingFee: props.getAdditionalFees?.result?.parkingFee || '',
        })
    }),
    graphql(getAdditionalFees, {
        name: 'getAdditionalFees',
        options: {
            fetchPolicy: 'network-only',
            ssr: false
        }
    }),
    graphql(updateAdditionalFees, {
        name: 'mutate'
    }),
    graphql(getSiteSettings, {
        name: 'getSiteSettings',
        options: {
            fetchPolicy: 'network-only',
            ssr: false
        }
    })
)(AdditionalFeesContainer);